import type { Phone } from '../types'
import { PhoneStateCard, type PhoneVisualState } from './PhoneStateCard'

interface Props {
  phones: Phone[]
  activeId: string | null
  onPick: (id: string) => void
  /** When true, all pickers are disabled (e.g. mid-call). */
  locked?: boolean
  /**
   * `grid` (default): 2-3 columns of thumbnail tiles — original layout.
   * `vertical`: column of full-width rows — used in the desktop sidebar and
   * the mobile Phones tab.
   */
  layout?: 'grid' | 'vertical'
}

/**
 * PhoneStateCard thumbnails acting as a phone selector.
 * - Offline phones are disabled.
 * - During an active call (`locked`) all phones except the active one are disabled.
 */
export function PhonePicker({ phones, activeId, onPick, locked, layout = 'grid' }: Props) {
  const containerClass =
    layout === 'vertical'
      ? 'flex flex-col gap-2'
      : 'grid grid-cols-2 sm:grid-cols-3 gap-3'

  return (
    <div className={containerClass}>
      {phones.map(p => {
        const isActive = p.id === activeId
        const state = mapState(p)
        const offline = p.status === 'offline'
        const disabled = offline || (locked === true && !isActive)
        const title = offline
          ? 'Трубка не на связи'
          : locked && !isActive
            ? 'Завершите текущий звонок'
            : undefined
        const baseClass = `rounded-xl border transition-colors text-left
              ${isActive
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'}
              ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:border-gray-300 dark:hover:border-gray-700 cursor-pointer'}
            `
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => !disabled && onPick(p.id)}
            disabled={disabled}
            title={title}
            aria-pressed={isActive}
            className={`${baseClass} ${layout === 'vertical' ? 'p-3 flex items-center gap-3' : 'p-2'}`}
          >
            {layout === 'vertical' ? (
              <>
                <PhoneStateCard state={state} size="sm" label={p.name} />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {p.name}
                  </span>
                  <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 truncate">
                    {p.sim}
                  </span>
                </div>
              </>
            ) : (
              <PhoneStateCard state={state} size="sm" label={p.name} />
            )}
          </button>
        )
      })}
    </div>
  )
}

function mapState(p: Phone): PhoneVisualState {
  if (p.status === 'offline') return 'offline'
  if (p.status === 'in-call' || p.currentCall) {
    if (p.currentCall?.state === 'ringing') return 'ringing'
    return 'in-call'
  }
  if (p.status === 'reconnecting') return 'offline'
  return 'normal'
}
